import { useEffect, useReducer } from "react";
import { useLaboratories } from "../../../contexts/LaboratoriesContext";
import Dictionary from "../../../models/Dictionary";
import { UserAssignable, UserAssignmentType } from "./models/userAssignment";
import { userGroupDictionary, userGroupOptions } from "./userGroups";

type State<T extends UserAssignmentType> = {
  description: string;
  disabledHint: string;
  label: string;
  options: UserAssignable<T>[];
  optionsDictionary: T extends "GROUP"
    ? Dictionary<UserAssignable<"GROUP">> | undefined
    : Dictionary<UserAssignable<"LAB">> | undefined;
  title: string;
};

const initialState = {
  description: "",
  disabledHint: "",
  label: "",
  options: [],
  optionsDictionary: undefined,
  title: "",
};

function reducer<T extends UserAssignmentType>(
  state: State<T>,
  action: {
    type: T;
    payload: {
      options: UserAssignable<T>[];
      optionsDictionary: T extends "GROUP"
        ? Dictionary<UserAssignable<"GROUP">>
        : Dictionary<UserAssignable<"LAB">> | undefined;
    };
  },
): State<T> {
  const { options, optionsDictionary } = action.payload;
  switch (action.type) {
    case "GROUP":
      return {
        description: "Assign groups for <user>.",
        disabledHint: "Change user groups to enable.",
        label: "User Groups",
        options,
        optionsDictionary,
        title: "Groups",
      };
    case "LAB":
      return {
        description: "Assign laboratories for <user>. This can affect access to existing test requests.",
        disabledHint: "Change user laboratories to enable.",
        label: "User Laboratories",
        options,
        optionsDictionary,
        title: "Laboratories",
      };
    case "MANAGED_LAB":
      return {
        description:
          "Assign laboratories managed by this <user>. This allows the user to edit lab affiliation of users.",
        disabledHint: "Change user laboratories to enable.",
        label: "Managed Laboratories",
        options,
        optionsDictionary,
        title: "Managed Laboratories",
      };
    default:
      return state;
  }
}

function useAssignmentDialog<T extends UserAssignmentType>(type: T) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { labsTechnical, labsDictionary } = useLaboratories();

  useEffect(() => {
    switch (type) {
      case "GROUP":
        dispatch({ type, payload: { options: userGroupOptions, optionsDictionary: userGroupDictionary } });
        break;
      case "LAB":
        dispatch({ type, payload: { options: labsTechnical, optionsDictionary: labsDictionary } });
        break;
      case "MANAGED_LAB":
        dispatch({
          type,
          payload: {
            options: labsTechnical,
            optionsDictionary: labsDictionary,
          },
        });
        break;
      default:
    }
  }, [type]);

  return state;
}

export default useAssignmentDialog;
